// Hooks específicos para Photos
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PhotoService } from '../services/photoService';
import { toastSuccess, toastError } from '@/lib/toast-utils';
import type {
    PhotoFilters,
    PhotoSearchParams,
    PhotoUploadData,
    PhotoUpdateData,
    PhotoWithDetails,
    PhotoStats,
    PhotoProcessingStatus
} from '../models/photo.types';
import type { Photo } from '@/types/schema';
import { api } from '@/services/api';

export const photoQueryKeys = {
    all: ['photos'] as const,
    list: (filters: PhotoFilters) => [...photoQueryKeys.all, 'list', { filters }] as const,
    detail: (id: string) => [...photoQueryKeys.all, 'detail', id] as const,
    latest: (limit: number) => [...photoQueryKeys.all, 'latest', limit] as const,
    search: (params: PhotoSearchParams) => [...photoQueryKeys.all, 'search', params] as const,
    byEvent: (eventId: string, page?: number, limit?: number) => [...photoQueryKeys.all, 'byEvent', eventId, page, limit] as const,
    related: (photoId: string, limit: number) => [...photoQueryKeys.all, 'related', photoId, limit] as const,
    batch: (batchId: string, page?: number, limit?: number, searchTerm?: string) => [...photoQueryKeys.all, 'batch', batchId, page, limit, searchTerm] as const,
    batchInfo: (batchId: string) => [...photoQueryKeys.all, 'batchInfo', batchId] as const,
    stats: () => [...photoQueryKeys.all, 'stats'] as const,
    processingStatus: (photoId: string) => [...photoQueryKeys.all, 'processingStatus', photoId] as const,
    withEvents: (limit: number) => [...photoQueryKeys.all, 'withEvents', limit] as const,
};

// Função para transformar as chaves da resposta da API (lowercase) para camelCase
const transformPhotoData = (data: any[]): Photo[] => {
  return data.map(item => ({
    id: item.id,
    eventId: item.eventid || item.eventId,
    photographerId: item.photographerid || item.photographerId,
    filename: item.filename,
    storageUrl: item.storageurl || item.storageUrl,
    thumbnailUrl: item.thumbnailurl || item.thumbnailUrl,
    watermarkedUrl: item.watermarkedurl || item.watermarkedUrl || item.storageurl || item.storageUrl,
    price: item.price ? Number(item.price) : 0,
    status: item.status || 'active',
    metadata: item.metadata,
    createdAt: item.createdat || item.createdAt || new Date().toISOString(),
    updatedAt: item.updatedat || item.updatedAt || new Date().toISOString(),
    resolution: item.resolution || 'original',
    uploadedAt: item.uploadedat || item.uploadedAt || new Date().toISOString(),
    processingStatus: item.processingstatus || item.processingStatus || 'ready',
    visibility: item.visibility || 'public',
    event: item.event ? {
      id: item.event.id,
      title: item.event.title,
      date: item.event.date,
      location: item.event.location,
      coverUrl: item.event.coverurl || item.event.coverUrl,
      status: item.event.status,
      pricePerPhoto: item.event.priceperphoto || item.event.pricePerPhoto,
      monetizationType: item.event.monetizationtype || item.event.monetizationType,
      platformCommissionRate: item.event.platformcommissionrate || item.event.platformCommissionRate || 0,
      photographerCommissionRate: item.event.photographercommissionrate || item.event.photographerCommissionRate || 0,
      createdAt: item.event.createdat || item.event.createdAt || new Date().toISOString(),
      updatedAt: item.event.updatedat || item.event.updatedAt || new Date().toISOString()
    } : undefined
  }));
};

// Hook para buscar todas as fotos com filtros
export function usePhotos(filters: PhotoFilters = {}) {
    return useQuery({
        queryKey: photoQueryKeys.list(filters),
        queryFn: () => PhotoService.getAll(filters),
        staleTime: 5 * 60 * 1000, // 5 minutos
    });
}
// Hook para buscar foto por ID
export function usePhoto(id: string) {
    return useQuery({
        queryKey: photoQueryKeys.detail(id),
        queryFn: () => PhotoService.getById(id),
        enabled: !!id,
        staleTime: 10 * 60 * 1000, // 10 minutos
    });
}

// Hook para buscar fotos mais recentes
export function useLatestPhotos(limit = 8) {
    return useQuery({
        queryKey: photoQueryKeys.latest(limit),
        queryFn: () => PhotoService.getLatest(limit),
        staleTime: 5 * 60 * 1000, // 5 minutos
    });
}

// Hook para buscar fotos por evento e número do peito (Search)
export function useSearchPhotos(params: PhotoSearchParams) {
  return useQuery({
    queryKey: photoQueryKeys.search(params),
    queryFn: async () => {
      // Construir query string
      const searchParams = new URLSearchParams();
      if (params.eventId) searchParams.append('eventId', params.eventId);
      if (params.bib) searchParams.append('bibNumber', params.bib);
      if (params.tags && params.tags.length > 0) {
        searchParams.append('tags', params.tags.join(','));
      }

      const { data } = await api.get(`/photos/search?${searchParams.toString()}`);
      
      // A API retorna { success: true, data: [...] }
      if (data.data && Array.isArray(data.data)) {
        return transformPhotoData(data.data);
      } else if (Array.isArray(data)) {
         return transformPhotoData(data);
      }
      
      return [];
    },
    enabled: !!(params.eventId || params.bib || (params.tags && params.tags.length > 0)),
    staleTime: 1000 * 60 * 5, // 5 minutos
  });
}

// Hook para buscar fotos por evento (otimizado por padrão)
export function usePhotosByEvent(eventId: string, page = 1, limit = 12, fullDetail: boolean = false) {
    return useQuery({
        queryKey: photoQueryKeys.byEvent(eventId, page, limit),
        queryFn: () => PhotoService.getByEvent(eventId, page, limit, fullDetail),
        enabled: !!eventId,
        staleTime: 5 * 60 * 1000, // 5 minutos
    });
}

// Hook para buscar fotos relacionadas
export function useRelatedPhotos(photoId: string, limit = 6) {
    return useQuery({
        queryKey: photoQueryKeys.related(photoId, limit),
        queryFn: () => PhotoService.getRelated(photoId, limit),
        enabled: !!photoId,
        staleTime: 10 * 60 * 1000, // 10 minutos
    });
}

// Hook para buscar fotos com eventos
export function usePhotosWithEvents(limit = 8) {
    return useQuery({
        queryKey: photoQueryKeys.withEvents(limit),
        queryFn: () => PhotoService.getPhotosWithEvents(limit),
        staleTime: 5 * 60 * 1000, // 5 minutos
    });
}

// Hook para buscar informações de batch
export function useBatchInfo(batchId: string) {
    return useQuery({
        queryKey: photoQueryKeys.batchInfo(batchId),
        queryFn: () => PhotoService.getBatchInfo(batchId),
        enabled: !!batchId,
        staleTime: 5 * 60 * 1000, // 5 minutos
    });
}

// Hook para buscar fotos de um batch
export function useBatchPhotos(batchId: string, page = 1, limit = 10, searchTerm = '') {
    return useQuery({
        queryKey: photoQueryKeys.batch(batchId, page, limit, searchTerm),
        queryFn: () => PhotoService.getBatchPhotos(batchId, page, limit, searchTerm),
        enabled: !!batchId,
        staleTime: 2 * 60 * 1000, // 2 minutos
    });
}

// Hook para buscar estatísticas
export function usePhotoStats() {
    return useQuery({
        queryKey: photoQueryKeys.stats(),
        queryFn: () => PhotoService.getStats(),
        staleTime: 10 * 60 * 1000, // 10 minutos
    });
}

// Hook para buscar status de processamento
export function useProcessingStatus(photoId: string) {
    return useQuery({
        queryKey: photoQueryKeys.processingStatus(photoId),
        queryFn: () => PhotoService.getProcessingStatus(photoId),
        enabled: !!photoId,
        refetchInterval: 2000, // Refetch a cada 2 segundos
        staleTime: 0, // Sempre considerar stale para updates em tempo real
    });
}

// Hook para atualizar foto
export function useUpdatePhoto() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (updateData: PhotoUpdateData) => PhotoService.update(updateData),
        onSuccess: (updatedPhoto) => {
            // Invalidar queries relacionadas
            queryClient.invalidateQueries({ queryKey: photoQueryKeys.detail(updatedPhoto.id) });
            queryClient.invalidateQueries({ queryKey: photoQueryKeys.all });

            toastSuccess({ title: 'Foto atualizada com sucesso!' });
        },
        onError: (error) => {
            console.error('Erro ao atualizar foto:', error);
            toastError({ title: 'Erro ao atualizar foto. Tente novamente.' });
        },
    });
}

// Hook para deletar foto
export function useDeletePhoto() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => PhotoService.delete(id),
        onSuccess: (_, photoId) => {
            // Remover da cache
            queryClient.removeQueries({ queryKey: photoQueryKeys.detail(photoId) });
            queryClient.invalidateQueries({ queryKey: photoQueryKeys.all });

            toastSuccess({ title: 'Foto removida com sucesso!' });
        },
        onError: (error) => {
            console.error('Erro ao deletar foto:', error);
            toastError({ title: 'Erro ao remover foto. Tente novamente.' });
        },
    });
}

// Hook para upload de fotos
export function useUploadPhotos() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (uploadData: PhotoUploadData) => PhotoService.upload(uploadData),
        onSuccess: (result) => {
            if (result.success) {
                // Invalidar queries relacionadas
                queryClient.invalidateQueries({ queryKey: photoQueryKeys.all });
                queryClient.invalidateQueries({ queryKey: photoQueryKeys.latest(8) });

                toastSuccess({ title: `${result.photos.length} foto(s) enviada(s) com sucesso!` });
            } else {
                toastError({ title: 'Erro no upload das fotos' });
            }
        },
        onError: (error) => {
            console.error('Erro no upload:', error);
            toastError({ title: 'Erro ao enviar fotos. Tente novamente.' });
        },
    });
}

// Hook para download de foto
export function useDownloadPhoto() {
    return useMutation({
        mutationFn: ({ photoId, options }: { photoId: string; options?: any }) =>
            PhotoService.download(photoId, options),
        onSuccess: (blob, { photoId }) => {
            // Criar link de download
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `photo-${photoId}.jpg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

            toastSuccess({ title: 'Download iniciado!' });
        },
        onError: (error) => {
            console.error('Erro no download:', error);
            toastError({ title: 'Erro ao baixar foto. Tente novamente.' });
        },
    });
}
